import { useState, type FormEvent } from 'react';
import { CheckCircle2, Check, Copy, ExternalLink, MapPin, PackageSearch, Search, Truck } from 'lucide-react';
import StorePage from './StorePage';
import { lookupOrdersByDocument, type Shipment, type TrackedOrder } from '@/services/orderLookup';
import { store } from '@/services/store';
import './order-lookup.css';

function formatDocument(value: string): string {
  const digits = value.replace(/\D/g, '').slice(0, 14);
  if (digits.length <= 11) {
    return digits
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d{1,2})$/, '$1-$2');
  }
  return digits
    .replace(/(\d{2})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1/$2')
    .replace(/(\d{4})(\d{1,2})$/, '$1-$2');
}

function money(value: string | null, currency: string | null): string {
  if (value == null) return '—';
  return Number(value).toLocaleString('pt-BR', { style: 'currency', currency: currency || 'BRL' });
}

function dateTime(value: string | null): string | null {
  if (!value) return null;
  return new Date(value).toLocaleString('pt-BR', { day: '2-digit', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' }).replace(' de ', ' de ');
}

// Código de rastreio e link: em destaque, com botão de copiar e de abrir o rastreamento
// (nos Correios, quando a transportadora é essa).
// Quando o envio existe mas o código ainda não chegou até aqui: em vez de só dizer "indisponível",
// leva o cliente a pedir o código à loja pelo WhatsApp, com a mensagem já pronta.
function MissingTracking({ order, carrier }: { order: TrackedOrder; carrier?: string | null }) {
  const text = `Olá! Meu pedido #${order.number} já foi enviado. Pode me passar o código de rastreio?`;
  return <div className="ol-tracking"><Truck size={16} /><div>
    {carrier && <span>{carrier}</span>}
    <strong>Seu pedido já foi enviado</strong>
    <small>O código de rastreio ainda não aparece aqui. Peça para a gente e enviamos na hora.</small>
    <div className="ol-track-actions">
      <a className="ol-track-btn primary" href={`${store.whatsapp}?text=${encodeURIComponent(text)}`} target="_blank" rel="noopener noreferrer"><ExternalLink size={14} />Pedir meu código no WhatsApp</a>
    </div>
  </div></div>;
}

function ShipmentTracking({ shipment, order }: { shipment: Shipment; order: TrackedOrder }) {
  const [copied, setCopied] = useState(false);
  const code = shipment.tracking_code;
  async function copy() {
    if (!code) return;
    try { await navigator.clipboard.writeText(code); } catch {
      const field = document.createElement('textarea');
      field.value = code; document.body.appendChild(field); field.select();
      try { document.execCommand('copy'); } catch { /* sem área de transferência */ }
      field.remove();
    }
    setCopied(true);
    window.setTimeout(() => setCopied(false), 2000);
  }
  if (!code) return <MissingTracking order={order} carrier={shipment.carrier} />;
  return <div className="ol-tracking">
    <Truck size={16} />
    <div>
      <span>{shipment.carrier || (shipment.correios ? 'Correios' : 'Transportadora')}</span>
      <strong className="ol-code">Código de rastreio: <code>{code}</code></strong>
      <div className="ol-track-actions">
        <button type="button" className="ol-track-btn" onClick={copy} aria-label={`Copiar código de rastreio ${code}`}>
          {copied ? <Check size={14} /> : <Copy size={14} />}{copied ? 'Código copiado' : 'Copiar código'}
        </button>
        {shipment.tracking_url && <a className="ol-track-btn primary" href={shipment.tracking_url} target="_blank" rel="noopener noreferrer">
          <ExternalLink size={14} />{shipment.correios ? 'Rastrear nos Correios' : 'Acompanhar entrega'}
        </a>}
      </div>
      {shipment.correios && <small>Depois da postagem, os Correios podem levar algumas horas para mostrar a movimentação.</small>}
    </div>
  </div>;
}

function OrderCard({ order }: { order: TrackedOrder }) {
  const firstImage = order.products.find(p => p.image)?.image;
  const shipments = order.fulfillments ?? (order.fulfillment ? [order.fulfillment] : []);
  const dispatched = /Enviado|Entregue/.test(order.status.label);
  return <section className="ol-result">
    <div className="ol-headline"><CheckCircle2 size={20} /><strong>{order.status.headline}</strong></div>
    {order.destination && <div className="ol-destination"><MapPin size={16} /><span>Destino: {order.destination}</span></div>}

    <div className="ol-grid">
      <div className="ol-left">
        <div className="ol-order-summary">
          {firstImage && <img src={firstImage} alt="" />}
          <div>
            <strong>Pedido #{order.number}</strong>
            <span>{order.products.length} produto{order.products.length === 1 ? '' : 's'}</span>
          </div>
        </div>

        <ul className="ol-events">
          {order.events.map((event, i) => <li key={i}>
            <CheckCircle2 size={16} />
            <div><span>{event.label}</span>{event.at && <small>{dateTime(event.at)}</small>}</div>
          </li>)}
        </ul>

        {shipments.map((shipment, i) => <ShipmentTracking key={`${shipment.tracking_code ?? 'sem-codigo'}-${i}`} shipment={shipment} order={order} />)}
        {shipments.length === 0 && dispatched && <MissingTracking order={order} />}
      </div>

      <div className="ol-right">
        <ul className="ol-products">
          {order.products.map((p, i) => <li key={i}>
            {p.image && <img src={p.image} alt="" />}
            <div className="ol-product-info">
              <span>{p.name}{p.quantity ? ` × ${p.quantity}` : ''}</span>
            </div>
            <div className="ol-product-price">
              {p.compare_at_price && Number(p.compare_at_price) > Number(p.price) && <s>{money(p.compare_at_price, order.currency)}</s>}
              <strong>{money(p.price, order.currency)}</strong>
            </div>
          </li>)}
        </ul>
        <div className="ol-totals">
          {order.subtotal && <div><span>Subtotal</span><span>{money(order.subtotal, order.currency)}</span></div>}
          {order.shipping_cost && <div><span>Frete</span><span>{money(order.shipping_cost, order.currency)}</span></div>}
          <div className="ol-total-row"><span>Total</span><span>{money(order.total, order.currency)}</span></div>
        </div>
      </div>
    </div>
  </section>;
}

export default function OrderLookup() {
  const [taxId, setTaxId] = useState('');
  const [orders, setOrders] = useState<TrackedOrder[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true); setError(''); setOrders(null);
    try {
      setOrders(await lookupOrdersByDocument(taxId));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Não foi possível consultar seus pedidos.');
    } finally {
      setBusy(false);
    }
  }

  return <StorePage title="Acompanhar pedido" subtitle="Consulte o status das suas compras com o seu CPF ou CNPJ.">
    <section className="ol-card">
      <PackageSearch size={30} strokeWidth={1.4} />
      <h2>Onde estão meus pedidos?</h2>
      <p>Informe o CPF ou CNPJ usado na compra para ver o status dos seus pedidos.</p>
      <form onSubmit={submit}>
        <label htmlFor="ol-document">CPF ou CNPJ</label>
        <input id="ol-document" type="text" inputMode="numeric" required value={taxId} onChange={e => setTaxId(formatDocument(e.target.value))} placeholder="000.000.000-00" maxLength={18} />
        <button className="ol-cta" disabled={busy}>{busy ? 'Consultando…' : 'Consultar pedidos'} <Search size={18} /></button>
      </form>
      {error && <p role="alert" className="ol-error">{error}</p>}
      <a className="ol-help" href={store.whatsapp} target="_blank" rel="noopener noreferrer">Não encontrou seu pedido? Fale conosco</a>
    </section>

    {orders && orders.length > 0 && <div className="ol-results" aria-live="polite">
      {orders.map((order) => <OrderCard order={order} key={order.number} />)}
    </div>}
  </StorePage>;
}
